import React, { useState, useCallback, useEffect } from "react";
import { InputTextProps } from "@clearabee/ui-library-native";
import { debounce } from "@clearabee/ui-sdk";
import { Input } from "components/Core/Input/Input";
import {
  useTooltip,
  UseTooltipProps,
  UseTooltipReturn,
} from "lib/hooks/useTooltip";
import { SurchargesProhibitedItems as SurchargesProhibitedItemsComponent } from "../ProductDetails/SurchargesProhibitedItems";
import { Overlay } from "./Overlay";

interface WasteDescriptionChildProps {
  DescriptionField: (props: InputTextProps) => React.ReactElement;
  SurchargesProhibitedItems: () => React.ReactElement;
  Tooltip: UseTooltipReturn["Tooltip"];
  Overlay: () => React.ReactElement | null;
  isOverlayShowing: boolean;
}

interface WasteDescriptionProps extends UseTooltipProps {
  children: (props: WasteDescriptionChildProps) => React.ReactElement;
}

export const WasteDescription = ({
  categorySku,
  surcharges,
  children,
}: WasteDescriptionProps): React.ReactElement => {
  const [extras, setExtras] = useState<Record<string, number>>({});
  const [description, setDescription] = useState<string>("");
  const { updateTooltip, Tooltip, isDisabled, ...tooltip } = useTooltip({
    categorySku,
    surcharges,
  });

  const hasProhibitedItems = ["MANVAN", "BINCOL", "SKIPS", "SKIPBAGS"].includes(
    categorySku,
  );
  const hasSurcharges =
    categorySku === "MANVAN" ||
    categorySku === "SKIPBAGS" ||
    categorySku.includes("SKIPBAG");

  const isOverlayShowing =
    !!(tooltip.surchargeItems.length || tooltip.prohibitedItems.length) &&
    !isDisabled;

  const handleSurchargeSelection = (sku: string, value: number) => {
    setExtras({ ...extras, [sku]: value });
  };

  const debouncedSetDescription = useCallback(
    debounce((value: string) => {
      setDescription(value);
    }, 300),
    [],
  );

  const DescriptionField: WasteDescriptionChildProps["DescriptionField"] =
    useCallback((props) => {
      const handleInputOnChange = (value: string) => {
        props?.onChange?.(value);
        debouncedSetDescription(value);
      };

      return <Input.Text {...props} onChange={handleInputOnChange} />;
    }, []);

  const SurchargesProhibitedItems = useCallback(
    () => (
      <SurchargesProhibitedItemsComponent
        surchargeItems={tooltip.surchargeItems}
        prohibitedItems={tooltip.prohibitedItems}
        onSurchargeSelection={handleSurchargeSelection}
        surcharges={surcharges}
        hasProhibitedItems={hasProhibitedItems}
        hasSurcharges={hasSurcharges}
        isSkipJob={categorySku === "SKIPS"}
      />
    ),
    [tooltip.surchargeItems, tooltip.prohibitedItems],
  );

  useEffect(() => {
    updateTooltip(description, extras);
  }, [description, extras]);

  return children({
    DescriptionField,
    SurchargesProhibitedItems,
    Tooltip,
    Overlay: () => <Overlay isVisible={isOverlayShowing} />,
    isOverlayShowing,
  });
};
